import { z } from "zod";

import type {
  CompletionChunk,
  CompletionUsage,
  GenerationRequest,
  GenerationResponse,
  LlmInferenceRequest,
} from "../openai";
import type {
  BatchInfo,
  InternalGeneration,
  StickyRoutingOutcome,
} from "./generations.models";
import type {
  InferenceTimeouts,
  StickyRouting,
} from "./generation-service.models";

/**
 * Schedules the successful-generation finalization publish for a streaming
 * request. When omitted, the router awaits the publish before returning; when
 * provided, the router hands the publish promise here so the terminal stream
 * event is not delayed by finalization.
 *
 * The handler MUST keep the promise alive until it settles. Runtimes that
 * cancel unregistered background work should register it with their
 * request-lifetime mechanism.
 */
export type FinalizationPublishScheduler = (publish: Promise<unknown>) => void;

/**
 * Receives the engine's sticky-routing outcome for a generation dispatched
 * with `stickyRouting: true`. Called exactly once per request: with the LOCK
 * event's sticky object when the engine reported one, otherwise with null
 * (worker error or a LOCK carrying no sticky data).
 */
export type StickyRoutingOutcomeReporter = (
  outcome: StickyRoutingOutcome | null,
) => void;

/**
 * Receives the id of the deployment instance serving a generation, from the
 * engine's LOCK event. Called at most once per request, and never when the
 * request fails before LOCK. inference-api stamps it on the response
 * (`SERVING_INSTANCE_ID_HEADER`, @inference-net/cf-compatible-models) so
 * finalization can attribute the inference to the deployment that served it.
 */
export type ServingInstanceReporter = (instanceId: string) => void;

/** Opt-in Engine-side SSE delta batching for an individual streaming request. */
export const ENGINE_SSE_BATCH_HEADER = "x-inference-engine-sse-batch";

/**
 * The request body's `model`, so the Engine can shed a request for a model
 * with no free capacity before it reads the body.
 */
export const ENGINE_MODEL_HEADER = "x-inference-model";

export const EngineSseBatchOptionsSchema = z
  .object({
    maxSize: z.number().int().min(1).max(32),
    flushIntervalMs: z.number().int().min(1).max(1_000),
  })
  .readonly();

export type EngineSseBatchOptions = z.infer<typeof EngineSseBatchOptionsSchema>;

/**
 * US routing affinity (INF-5383): forwarded to elixir-engine as
 * `required_geo` only when set; null keeps the engine payload byte-identical
 * to ordinary traffic.
 */
export type RequiredGeo = "US" | null;

export type InferenceRouterInterface = {
  waitingGeneration(params: {
    generation: InternalGeneration;
    generationRequest: GenerationRequest;
    generationRequestUrl: string | null;
    generationRoutingIdentifier: string | null;
    /**
     * Fires when the end client disconnects. Propagated upstream so the
     * elixir-engine request is cancelled instead of running a generation
     * nobody reads. Pass null when there is no client connection to watch
     * (e.g. queue-driven slow dispatch).
     */
    clientAbortSignal: AbortSignal | null;
    /** Non-null dispatches with automatic sticky routing (REQUEST_OBJECT + hash-based instance affinity). */
    stickyRouting: StickyRouting | null;
    /** Publish a billable cancelled result (partial usage) on client abort. */
    incrementalUsageBilling: boolean;
    inferenceTimeouts: InferenceTimeouts;
    /** See {@link StickyRoutingOutcomeReporter}. Pass null when the caller does not consume the outcome. */
    onStickyRoutingOutcome: StickyRoutingOutcomeReporter | null;
    /** See {@link RequiredGeo}. */
    requiredGeo?: RequiredGeo;
    /** See {@link FinalizationPublishScheduler}. */
    onFinalizationPublish?: FinalizationPublishScheduler;
    /** See {@link ServingInstanceReporter}. */
    onServingInstance?: ServingInstanceReporter;
  }): Promise<GenerationResponse>;
  /**
   * Resolves once elixir-engine has accepted the request and opened its
   * stream; rejects with the mapped engine error when it refuses before
   * dispatch (429 `capacity_exceeded`, 503 `no_workers`). Callers that own the
   * client's response status must await this before committing headers
   * (INF-5306).
   *
   * The stream's return value is the generation's final internal usage
   * (null when no chunk carried any), whether or not the client asked for
   * usage chunks via `stream_options.include_usage`.
   */
  streamingLlmInference(params: {
    generation: InternalGeneration;
    generationRequest: LlmInferenceRequest;
    generationRequestUrl: string | null;
    generationRoutingIdentifier: string | null;
    /** See {@link waitingGeneration}. */
    clientAbortSignal: AbortSignal | null;
    /** See {@link waitingGeneration}. */
    stickyRouting: StickyRouting | null;
    /** See {@link waitingGeneration}. */
    incrementalUsageBilling: boolean;
    inferenceTimeouts: InferenceTimeouts;
    /** See {@link StickyRoutingOutcomeReporter}. Pass null when the caller does not consume the outcome. */
    onStickyRoutingOutcome: StickyRoutingOutcomeReporter | null;
    /**
     * Optional Engine-side SSE delta batching. Omitted requests retain the
     * legacy one-frame-per-delta behavior.
     */
    engineSseBatch?: EngineSseBatchOptions;
    /** See {@link RequiredGeo}. */
    requiredGeo?: RequiredGeo;
    /** See {@link FinalizationPublishScheduler}. */
    onFinalizationPublish?: FinalizationPublishScheduler;
    /** See {@link ServingInstanceReporter}. */
    onServingInstance?: ServingInstanceReporter;
  }): Promise<AsyncGenerator<CompletionChunk, CompletionUsage | null>>;
  submitSlowGeneration(params: {
    generation: InternalGeneration;
    batchInfo: BatchInfo | null;
    expiresAt: Date | null;
    generationRequest: GenerationRequest;
    generationRequestUrl: string | null;
  }): Promise<void>;
};
